import * as React from 'react';
import { areAllTypesTheSame } from '../../utils';
import { getTypeDisplayName, isPrimitiveType } from '../utils';
import {
  getCallbackIfNeeded,
  getCallbackLink,
  shouldPresentTypeAsCallbackLink,
} from './utils';
import { Text, WEIGHTS, SIZES } from '../../../PageElements/Text';
import { CustomLink } from '../../../PageElements/CustomLink';
import {
  Type,
  TypeInServiceJson,
  LegacySingleType,
  isLegacyType,
  TypeParameter,
} from '../../typeParser';
import { StoreContext } from '../../../../appContext/StoreProvider';
import { TypeDisplay } from '../../../LibraryView/ApiSectionView/PropertiesList/Property/TypeDisplay';

interface NestedTableSingleTypeDisplayProps {
  type: Type | LegacySingleType;
  metaData: Object;
  path: string;
  shouldDisplayDropdown?: boolean;
  onTypeClicked?: () => void;
  JSLayoutType?: boolean;
}

/**
 *
 * @param type - a single type, LegacyType | Type
 * @param metaData
 * @param path
 * @param shouldDisplayDropdown
 * @param onTypeClicked
 * @constructor
 */
const NestedTableSingleTypeDisplay = ({
  type,
  metaData,
  path,
  shouldDisplayDropdown = false,
  onTypeClicked,
  JSLayoutType,
}: NestedTableSingleTypeDisplayProps) => {
  const { store } = React.useContext(StoreContext);
  const typeDisplayName: string = getTypeDisplayName({
    type,
    store,
    JSLayoutType,
    typeDisplayName: (type as TypeParameter).displayName,
  });

  if (shouldDisplayDropdown) {
    const shouldShowTypeAsButton = isLegacyType(type)
      ? !isPrimitiveType(type, store)
      : type.referenceType; // should show the type as a button if the type is not a primitive.
    if (shouldShowTypeAsButton) {
      return (
        <Text
          inline
          size={SIZES.small}
          link
          weight={WEIGHTS.normal}
          tagName="span"
          dataHook="type-button"
          onClick={(e) => {
            e.stopPropagation();
            onTypeClicked();
          }}
        >
          {typeDisplayName}
        </Text>
      );
    }
  }

  if (shouldPresentTypeAsCallbackLink(metaData)) {
    const callback = getCallbackIfNeeded(type, metaData, store);
    if (callback) {
      return (
        <Text
          size={SIZES.small}
          link
          weight={WEIGHTS.normal}
          dataHook="type-link"
          tagName="span"
        >
          <CustomLink to={getCallbackLink(callback, path)} smooth={true}>
            {typeDisplayName}
          </CustomLink>
        </Text>
      );
    }
  }
  return (
    <TypeDisplay
      type={type}
      forTypeTableColumn={true}
      JSLayoutType={JSLayoutType}
      displayName={(type as TypeParameter).displayName}
    />
  );
};

export const NestedTableTypeDisplay = ({
  type,
  metaData,
  path,
  onTypeClicked,
  JSLayoutType,
}: {
  type: TypeInServiceJson;
  metaData: Object;
  path: string;
  onTypeClicked: (typeIndex: number) => () => void;
  JSLayoutType?: boolean;
}) => {
  const { store } = React.useContext(StoreContext);

  if (Array.isArray(type)) {
    return (type as (Type | LegacySingleType)[]).map(
      (singleType, typeIndex) => {
        return (
          <React.Fragment key={typeIndex}>
            <NestedTableSingleTypeDisplay
              type={singleType}
              metaData={metaData}
              path={path}
              shouldDisplayDropdown={!areAllTypesTheSame(type, store)}
              onTypeClicked={onTypeClicked(typeIndex)}
              JSLayoutType={JSLayoutType}
            />
            {typeIndex !== type.length - 1 ? ' | ' : ''}
          </React.Fragment>
        );
      },
    );
  } else {
    return (
      <NestedTableSingleTypeDisplay
        type={type}
        metaData={metaData}
        path={path}
        JSLayoutType={JSLayoutType}
      />
    );
  }
};
